// src/pages/LabWorkspacePage.jsx
// Workspace apprenant v2 — Timer + Grading + NetBot + Credits + i18n
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams }    from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse,
  Divider, IconButton, LinearProgress, Paper,
  Stack, Tooltip, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Snackbar,
} from "@mui/material";
import ArrowBackIcon        from "@mui/icons-material/ArrowBack";
import RefreshIcon          from "@mui/icons-material/Refresh";
import PlayArrowIcon        from "@mui/icons-material/PlayArrow";
import StopIcon             from "@mui/icons-material/Stop";
import PauseIcon            from "@mui/icons-material/Pause";
import TerminalIcon         from "@mui/icons-material/Terminal";
import LanguageIcon         from "@mui/icons-material/Language";
import DesktopWindowsIcon   from "@mui/icons-material/DesktopWindows";
import GradeIcon            from "@mui/icons-material/Grade";
import AccessTimeIcon       from "@mui/icons-material/AccessTime";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import CancelIcon           from "@mui/icons-material/Cancel";
import WarningAmberIcon     from "@mui/icons-material/WarningAmber";

import { useTranslation }   from "react-i18next";
import api, { getUserMessage } from "../api/axios";
import ConsoleManager       from "../components/workspace/ConsoleManager";
import TopologyViewer       from "../components/topology/TopologyViewer";
import NetBotWidget         from "../components/NetBotWidget";
import { getGradingResults, submitGrading, getLabGradingConfig } from "../api/gradingApi";
import { launchLab, extendLab } from "../api/labLaunchApi";
import ContextualOnboarding from "../components/ContextualOnboarding";

// ── Helpers ───────────────────────────────────────────────────────

function toErrorString(e) {
  const msg = getUserMessage(e, "Unknown error");
  const detail = e?.response?.data?.detail;
  if (!detail) return msg;
  try { return typeof detail === "string" ? msg + " — " + detail : msg + " — " + JSON.stringify(detail); }
  catch { return msg; }
}

const isSuspended = s => ["suspended","paused"].includes(String(s||"").toLowerCase());
const isStarted   = s => ["started","running"].includes(String(s||"").toLowerCase());

function fmtDuration(ms) {
  if (!ms || ms <= 0) return "00:00";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return h + "h " + String(m % 60).padStart(2,"0") + "m";
  return String(m).padStart(2,"0") + ":" + String(s % 60).padStart(2,"0");
}

// ── Composant Timer Header ────────────────────────────────────────

function TimerChip({ msLeft, msTotal, t }) {
  const expired  = msLeft != null && msLeft <= 0;
  const urgent   = msLeft != null && msLeft > 0 && msLeft < 10 * 60 * 1000;
  const warning  = msLeft != null && msLeft > 0 && msLeft < 20 * 60 * 1000 && !urgent;
  const pct      = (msLeft != null && msTotal) ? Math.min(100, Math.max(0, (msLeft / msTotal) * 100)) : null;

  const color = expired ? "#ef4444" : urgent ? "#dc2626" : warning ? "#d97706" : "#16a34a";
  const bg    = expired ? "#fef2f2"  : urgent ? "#fef2f2"  : warning ? "#fffbeb"  : "#f0fdf4";
  const bc    = expired ? "#fecaca"  : urgent ? "#fecaca"  : warning ? "#fde68a"  : "#bbf7d0";

  return (
    <Box sx={{ px: 2, py: 0.75, borderRadius: 2, bgcolor: bg, border: "1px solid", borderColor: bc, minWidth: 110 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <AccessTimeIcon sx={{ fontSize: 14, color }} />
        <Typography fontSize={13} fontWeight={700} color={color}>
          {msLeft == null ? "--:--" : expired ? t("expired_label", { ns: "workspace" }) : fmtDuration(msLeft)}
        </Typography>
      </Stack>
      {pct != null && (
        <LinearProgress variant="determinate" value={pct}
          sx={{ mt: 0.5, height: 3, borderRadius: 2, bgcolor: bc,
            "& .MuiLinearProgress-bar": { bgcolor: color, transition: "width 1s linear" } }} />
      )}
    </Box>
  );
}

// ── Composant Grading Panel ───────────────────────────────────────

function GradingPanel({ reservationId, labId, t }) {
  const [config,     setConfig]     = useState(null);
  const [results,    setResults]    = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");
  const [open,       setOpen]       = useState(false);

  useEffect(() => {
    if (!labId) return;
    getLabGradingConfig(labId)
      .then(r => { if (r.data?.enabled) setConfig(r.data); })
      .catch(() => {});
    getGradingResults(reservationId)
      .then(r => setResults(r.data?.results || []))
      .catch(() => {});
  }, [reservationId, labId]);

  if (!config?.enabled) return null;

  const latest   = results[0];
  const checks   = config?.checks || [];
  const passed   = latest?.passed;
  const score    = latest?.score;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const r = await submitGrading(reservationId);
      setResults(prev => [r.data, ...prev]);
    } catch(e) {
      setError(e?.response?.data?.detail?.message || t("submitError", { ns: "workspace" }));
    } finally { setSubmitting(false); }
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider",
          bgcolor: latest ? (passed ? "#f0fdf4" : "#fef2f2") : "#f8fafc",
          cursor: "pointer" }}
        onClick={() => setOpen(o => !o)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <GradeIcon sx={{ fontSize: 18, color: latest ? (passed ? "#16a34a" : "#ef4444") : "#64748b" }} />
          <Typography fontSize={13} fontWeight={600}>{t("grading", { ns: "workspace" })}</Typography>
          {latest && (
            <Chip
              size="small"
              label={score?.toFixed(1) + "%"}
              color={passed ? "success" : "error"}
              sx={{ height: 20, fontSize: 11 }}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="contained" disableElevation
            onClick={e => { e.stopPropagation(); handleSubmit(); }}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={12} /> : null}
            sx={{ borderRadius: 2, fontSize: 11, bgcolor: "#0f766e", "&:hover": { bgcolor: "#0d6761" } }}
          >
            {t("gradingSubmit", { ns: "workspace" })}
          </Button>
        </Stack>
      </Stack>

      <Collapse in={open}>
        <Box sx={{ p: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

          {checks.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography fontSize={12} color="text.secondary" fontWeight={500} mb={1}>
                {t("checksCriteria", { ns: "workspace", defaultValue: "Criteres d evaluation" })}
              </Typography>
              <Stack spacing={0.75}>
                {checks.map((c, i) => {
                  const checkResult = latest?.details?.checks?.[i];
                  const isPassed    = checkResult?.passed;
                  const hasResult   = checkResult !== undefined;
                  return (
                    <Stack key={c.id || i} direction="row" spacing={1} alignItems="center">
                      {hasResult
                        ? isPassed
                          ? <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                          : <CancelIcon      sx={{ fontSize: 16, color: "#ef4444" }} />
                        : <Box sx={{ width: 16, height: 16, borderRadius: "50%",
                            border: "1.5px solid #94a3b8" }} />
                      }
                      <Typography fontSize={12}>{c.description || c.id}</Typography>
                      <Typography fontSize={11} color="text.secondary">({c.weight} pts)</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}

          {results.length > 0 && (
            <Box>
              <Typography fontSize={12} color="text.secondary" fontWeight={500} mb={0.75}>
                {t("lastAttempt", { ns: "workspace", defaultValue: "Dernier essai" })}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <LinearProgress variant="determinate" value={Math.min(latest.score, 100)}
                  sx={{ flex: 1, height: 6, borderRadius: 3,
                    bgcolor: passed ? "#bbf7d0" : "#fecaca",
                    "& .MuiLinearProgress-bar": { bgcolor: passed ? "#16a34a" : "#ef4444" } }} />
                <Typography fontSize={13} fontWeight={700} color={passed ? "#16a34a" : "#ef4444"}>
                  {latest.score?.toFixed(1)}%
                </Typography>
                <Chip label={passed ? t("gradingPassed", { ns: "workspace" }) : t("gradingFailed", { ns: "workspace" })}
                  color={passed ? "success" : "error"} size="small" />
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Composant Extend Dialog ───────────────────────────────────────

function ExtendDialog({ open, onClose, onConfirm, t }) {
  const [minutes, setMinutes] = useState(30);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
        {t("extend", { ns: "workspace" })}
      </DialogTitle>
      <DialogContent>
        <Typography fontSize={13} color="text.secondary" mb={2}>
          {t("extendDesc", { ns: "workspace", defaultValue: "Combien de minutes supplementaires souhaitez-vous ajouter ?" })}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {[5, 15, 30, 60, 120].map(m => (
            <Chip key={m} label={m + " min"} onClick={() => setMinutes(m)}
              color={minutes === m ? "primary" : "default"} sx={{ cursor: "pointer" }} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>{t("cancel", { ns: "common", defaultValue: "Annuler" })}</Button>
        <Button variant="contained" disableElevation onClick={() => onConfirm(minutes)}
          sx={{ borderRadius: 2, bgcolor: "#0f766e", "&:hover": { bgcolor: "#0d6761" } }}>
          {t("confirm", { ns: "common", defaultValue: "Confirmer" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Page principale ───────────────────────────────────────────────

export default function LabWorkspacePage() {
  const { t }              = useTranslation(["workspace", "common"]);
  const navigate           = useNavigate();
  const { reservationId }  = useParams();

  const rid = useMemo(() => {
    const n = Number(reservationId);
    return Number.isFinite(n) ? n : null;
  }, [reservationId]);

  const [loading,    setLoading]    = useState(true);
  const [payload,    setPayload]    = useState(null);
  const [nodes,      setNodes]      = useState([]);
  const [error,      setError]      = useState("");
  const [busy,       setBusy]       = useState(false);
  const [nodeBusy,   setNodeBusy]   = useState({});
  const [topoNodes,  setTopoNodes]  = useState([]);
  const [topoLinks,  setTopoLinks]  = useState([]);
  const [topoLoading,setTopoLoading]= useState(false);
  const [topoError,  setTopoError]  = useState("");
  const [extendOpen, setExtendOpen] = useState(false);
  const [snack,      setSnack]      = useState("");
  const [now,        setNow]        = useState(Date.now());
  // true une fois la fin réelle de la session détectée (STOP externe inclus) —
  // distinct de "msLeft <= 0", qui ne couvre que l'expiration naturelle du
  // créneau et ne détecte jamais un arrêt déclenché depuis une autre page
  // (ex. Reservations.js) pendant que ce Workspace reste affiché.
  const [ended,       setEnded]     = useState(false);

  const consoleApiRef = useRef(null);
  const retryTimerRef = useRef(null);
  const nodesSectionRef = useRef(null);
  const extendButtonRef = useRef(null);

  const host      = payload?.host       || "";
  const projectId = payload?.project_id || "";
  const labName   = payload?.lab_name   || payload?.reservation?.lab_name || null;
  const labId     = payload?.lab_id     || payload?.reservation?.lab_id   || null;
  const effectiveLabId = labId || (nodes.length > 0 ? payload?.lab_id : null);
  const endTime   = payload?.end_time   || payload?.reservation?.end_time   || null;
  const startTime = payload?.start_time || payload?.reservation?.start_time || null;
  const msLeft    = ended ? 0 : (endTime ? new Date(endTime).getTime() - now : null);
  const msTotal   = (endTime && startTime) ? new Date(endTime).getTime() - new Date(startTime).getTime() : null;
  const urgent    = msLeft != null && msLeft > 0 && msLeft < 10 * 60 * 1000;

  // Timer. Arrêté dès `ended` pour éviter un tick résiduel une fois la
  // session confirmée terminée (le countdown est de toute façon forcé à 0
  // via `msLeft` ci-dessus tant que `ended` est vrai).
  useEffect(() => {
    if (ended) return;
    const id = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (endTime && n > new Date(endTime).getTime()) {
        clearInterval(id);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [ended]);

  // Alerte 10 min
  const alertShownRef = useRef(false);
  useEffect(() => {
    if (urgent && !alertShownRef.current) {
      alertShownRef.current = true;
      setSnack(t("alertLow", { ns: "workspace" }));
    }
  }, [urgent, t]);

  // Statuts terminaux d'une réservation : une fois atteints, le Workspace ne
  // doit plus jamais se comporter comme si la session pouvait redevenir
  // active (pas de retry "lab en démarrage", pas de nouveau polling).
  const TERMINAL_STATUSES = ["completed", "cancelled", "canceled", "failed", "stopped"];

  const load = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    if (!rid) { setError("Invalid reservation id"); setLoading(false); return; }
    clearTimeout(retryTimerRef.current);
    if (!silent) { setLoading(true); setError(""); }
    try {
      const res  = await api.get("/gns3/reservations/" + rid + "/nodes");
      const data = res?.data || null;
      setPayload(data);
      setNodes(Array.isArray(data?.nodes) ? data.nodes : []);
    } catch(e) {
      const status = e?.response?.status;
      const rd     = e?.response?.data?.detail;
      const detail = typeof rd === "string" ? rd : rd?.message || "";
      if (status === 409) {
        // Le backend inclut le statut réel dans le message ("... status=completed").
        // Un statut terminal signifie que la session a été arrêtée (potentiellement
        // depuis un autre écran, ex. Reservations.js) : il ne faut jamais
        // l'interpréter comme "encore en démarrage", sans quoi le Workspace
        // resterait à retenter indéfiniment un lab qui ne redémarrera jamais.
        const statusMatch = detail.match(/status=([a-z_]+)/i);
        const reportedStatus = statusMatch ? statusMatch[1].toLowerCase() : null;
        if (reportedStatus && TERMINAL_STATUSES.includes(reportedStatus)) {
          setEnded(true);
          setError(t("lab_ended", { ns: "workspace", defaultValue: "Session terminée." }));
          setLoading(false);
          return;
        }
        if (!silent) {
          const msg = detail.includes("not running") ? t("lab_starting", { ns: "workspace", defaultValue: "Lab en demarrage..." })
            : detail.includes("project_id") ? t("lab_creating", { ns: "workspace", defaultValue: "Projet en creation..." })
            : t("lab_unavailable", { ns: "workspace", defaultValue: "Lab temporairement indisponible" });
          setError(msg);
          retryTimerRef.current = setTimeout(() => load(), 5000);
        }
      } else if (!silent) {
        setError(toErrorString(e));
      }
    } finally { setLoading(false); }
  }, [rid, t]);

  // Resynchronisation périodique légère (silencieuse, sans spinner) : détecte
  // un arrêt de la réservation déclenché depuis un autre écran pendant que ce
  // Workspace reste ouvert. S'arrête dès que la session est confirmée
  // terminée (pas de polling résiduel une fois `ended`).
  useEffect(() => {
    if (!rid || ended) return;
    const id = setInterval(() => { load({ silent: true }); }, 15000);
    return () => clearInterval(id);
  }, [rid, ended, load]);

  const loadTopology = useCallback(async () => {
    if (!rid) return;
    setTopoLoading(true); setTopoError("");
    try {
      const res  = await api.get("/gns3/reservations/" + rid + "/topology");
      const data = res?.data || {};
      const nsm  = {};
      nodes.forEach(n => { nsm[n.node_id] = n.status; });
      setTopoNodes((data.nodes || []).map(n => ({
        ...n, status: nsm[n.node_id] ?? n.status ?? "unknown",
        console_type: n.console_type || "", console_port: n.console || n.console_port || null,
      })));
      setTopoLinks(data.links || []);
    } catch(e) {
      if (e?.response?.status !== 409) setTopoError(toErrorString(e));
    } finally { setTopoLoading(false); }
  }, [rid, nodes]);

  const loadCalledRef = useRef(false);
  useEffect(() => {
    if (loadCalledRef.current) return;
    loadCalledRef.current = true;
    load();
    return () => clearTimeout(retryTimerRef.current);
  }, [load]);

  useEffect(() => { if (nodes.length > 0) loadTopology(); }, [nodes.length]);

  const runNodeAction = useCallback(async (nodeId, action) => {
    if (!rid || !nodeId) return;
    setNodeBusy(m => ({ ...m, [nodeId]: action }));
    setError("");
    try { await api.post("/gns3/reservations/" + rid + "/nodes/" + nodeId + "/" + action); await load(); }
    catch(e) { setError(toErrorString(e)); }
    finally   { setNodeBusy(m => { const { [nodeId]: _, ...r } = m; return r; }); }
  }, [rid, load]);

  const startAll = useCallback(async () => {
    setBusy(true); setError("");
    try { await api.post("/gns3/reservations/" + rid + "/nodes/start-all"); await load(); }
    catch(e) { setError(toErrorString(e)); }
    finally   { setBusy(false); }
  }, [rid, load]);

  const stopAll = useCallback(async () => {
    setBusy(true); setError("");
    try { await api.post("/gns3/reservations/" + rid + "/nodes/stop-all"); await load(); }
    catch(e) { setError(toErrorString(e)); }
    finally   { setBusy(false); }
  }, [rid, load]);

  const openTelnet = useCallback(n => consoleApiRef.current?.openTelnet?.(n), []);
  const openVnc    = useCallback(n => consoleApiRef.current?.openVnc?.(n), []);
  const openWeb    = useCallback(n => { if (n?.node_id) navigate("/lab/" + rid + "/console/" + n.node_id); }, [navigate, rid]);

  const handleTopologyNodeClick = useCallback(n => {
    const ct = String(n.console_type || "").toLowerCase();
    if (ct === "telnet" && n.console_port) openTelnet(n);
    else if (ct === "vnc" && n.console_port) openVnc(n);
  }, [openTelnet, openVnc]);

  const handleExtend = async (minutes) => {
    setExtendOpen(false);
    console.log("[EXTEND] rid=", rid, "labId=", labId, "payload=", payload?.lab_id);
    try {
      if (rid && labId) {
        await extendLab(labId, rid, minutes);
      } else {
        console.warn("[EXTEND] labId manquant, fallback launchLab");
        await launchLab(labId, minutes);
      }
      setSnack(t("extendSuccess", { ns: "workspace", defaultValue: "Session prolongee de " + minutes + " min" }));
      await load();
    } catch(e) {
      setSnack(toErrorString(e));
    }
  };

  const startedCount = nodes.filter(n => !!n.is_started || isStarted(n.status)).length;
  const stoppedCount = nodes.length - startedCount;
  const netbotContext = JSON.stringify({
    lab_name: labName || null,
    lab_id: labId || null,
    reservation_id: rid || null,
    nodes: nodes.map(n => ({ name: n.name, node_type: n.node_type, status: n.status })),
    lab_status: payload?.status || null,
  });

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: { xs: 1.5, md: 2.5 } }}>

      {/* ── Header ── */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}
        justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>

        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => navigate("/reservations")} size="small"
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {labName || t("reservation", { ns: "workspace", defaultValue: "Reservation" }) + " #" + (rid ?? "?")}
              </Typography>
              {nodes.length > 0 && (
                <Stack direction="row" spacing={0.5}>
                  <Box sx={{ px: 1.5, py: 0.4, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                    <Typography variant="caption" fontWeight={700} color="#22c55e">{startedCount} ON</Typography>
                  </Box>
                  {stoppedCount > 0 && (
                    <Box sx={{ px: 1.5, py: 0.4, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                      <Typography variant="caption" fontWeight={700} color="#94a3b8">{stoppedCount} OFF</Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
            {projectId && (
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace" }}>
                {projectId.slice(0, 8)}… {host && "· " + host}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TimerChip msLeft={msLeft} msTotal={msTotal} t={t} />

          <Button size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />}
            ref={extendButtonRef}
            onClick={() => setExtendOpen(true)}
            sx={{ borderRadius: 2, fontSize: 11, borderColor: "#0f766e", color: "#0f766e",
              "&:hover": { borderColor: "#0d6761", bgcolor: "#f0fdf4" } }}>
            {t("extend", { ns: "workspace" })}
          </Button>

          <Button size="small" startIcon={busy ? <CircularProgress size={14} /> : <RefreshIcon />}
            variant="outlined" onClick={async () => { setBusy(true); await load(); setBusy(false); }}
            disabled={busy} sx={{ borderRadius: 2, fontSize: 11 }}>
            {t("refresh", { defaultValue: "Rafraichir" })}
          </Button>
        </Stack>
      </Stack>

      {/* Alerte urgent */}
      {urgent && (
        <Alert severity="warning" icon={<WarningAmberIcon />}
          sx={{ mb: 2, borderRadius: 2, fontWeight: 500 }}>
          {t("alertLow", { ns: "workspace" })}
        </Alert>
      )}

      {error && (
        <Alert severity={error.includes("demarrage") || error.includes("creation") ? "info" : "error"}
          sx={{ mb: 2, borderRadius: 2 }}
          action={error.includes("demarrage") || error.includes("creation") ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
            </Stack>
          ) : null}>
          {error}
        </Alert>
      )}

      {/* ── Layout principal ── */}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ mb: 2 }}>

        {/* Colonne gauche: Noeuds + Grading */}
        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>

          {/* Grading panel */}
          <GradingPanel reservationId={rid} labId={labId} t={t} />

          {/* Noeuds */}
          <Paper elevation={0} sx={{
            flex: 1, border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden",
          }}>
            <Stack ref={nodesSectionRef} direction="row" alignItems="center" justifyContent="space-between"
              sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>
                  {t("nodes", { defaultValue: "Noeuds" })}
                </Typography>
                {!loading && nodes.length > 0 && (
                  <Chip size="small" label={nodes.length} sx={{ height: 18, fontSize: 11 }} />
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />}
                  onClick={startAll} disabled={busy} sx={{ borderRadius: 2, fontSize: 11 }}>
                  {t("start_all", { defaultValue: "Tout demarrer" })}
                </Button>
                <Button size="small" variant="outlined" startIcon={<StopIcon />}
                  onClick={stopAll}  disabled={busy} sx={{ borderRadius: 2, fontSize: 11 }}>
                  {t("stop_all", { defaultValue: "Tout arreter" })}
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ px: 2.5, py: 1 }}>
              {loading && (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {t("loading_nodes", { defaultValue: "Chargement des noeuds..." })}
                  </Typography>
                </Stack>
              )}
              {!loading && nodes.length === 0 && (
                <Alert severity="warning" sx={{ my: 1, borderRadius: 2 }}>
                  {t("no_nodes", { defaultValue: "Aucun noeud trouve" })}
                </Alert>
              )}
              {!loading && nodes.map(n => {
                const nodeId     = n.node_id;
                const started    = !!n.is_started || isStarted(n.status);
                const suspended  = isSuspended(n.status);
                const busyAction = nodeBusy[nodeId] || "";
                const anyBusy    = !!busyAction;
                const ct         = String(n.console_type || "none").toLowerCase();
                const canTelnet  = ct === "telnet" && !!n.console_port && started;
                const canVnc     = ct === "vnc"    && !!n.console_port && started;
                const canWeb     = (ct === "telnet" || ct === "vnc") && !!n.console_port && started;

                return (
                  <Box key={nodeId} sx={{
                    py: 1.5, display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: 2,
                    borderBottom: "1px solid", borderColor: "divider",
                    "&:last-child": { borderBottom: 0 },
                    "&:hover": { bgcolor: "#f8fafc" },
                    mx: -2.5, px: 2.5, transition: "background .15s",
                  }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{
                        width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                        bgcolor: started ? "#22c55e" : suspended ? "#f59e0b" : "#94a3b8",
                        boxShadow: started ? "0 0 0 3px #dcfce7" : "none",
                      }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" fontWeight={700}>{n.name}</Typography>
                          <Box sx={{
                            px: 1, py: 0.2, borderRadius: 1, fontSize: 10, fontWeight: 600,
                            bgcolor: started ? "#f0fdf4" : "#f8fafc",
                            color: started ? "#22c55e" : "#94a3b8",
                            border: "1px solid", borderColor: started ? "#bbf7d0" : "#e2e8f0",
                          }}>
                            {String(n.status || "unknown").toUpperCase()}
                          </Box>
                          {ct !== "none" && (
                            <Box sx={{ px: 1, py: 0.2, borderRadius: 1, fontSize: 10, fontWeight: 600,
                              bgcolor: "#eef2ff", color: "#6366f1", border: "1px solid #c7d2fe" }}>
                              {ct.toUpperCase()}{n.console_port ? ":" + n.console_port : ""}
                            </Box>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace", fontSize: 10 }}>
                          {nodeId}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                      {anyBusy && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                      <Tooltip title={suspended ? t("node_resume", { defaultValue: "Reprendre" }) : t("node_start", { defaultValue: "Demarrer" })}><span>
                        <IconButton size="small"
                          onClick={() => runNodeAction(nodeId, "start")}
                          disabled={anyBusy || (started && !suspended)}
                          sx={{ color: "#22c55e", "&:hover": { bgcolor: "#f0fdf4" }, "&.Mui-disabled": { opacity: .3 } }}>
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                      <Tooltip title="Stop"><span>
                        <IconButton size="small"
                          onClick={() => runNodeAction(nodeId, "stop")}
                          disabled={anyBusy || (!started && !suspended)}
                          sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" }, "&.Mui-disabled": { opacity: .3 } }}>
                          <StopIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                      <Tooltip title="Pause"><span>
                        <IconButton size="small"
                          onClick={() => runNodeAction(nodeId, "suspend")}
                          disabled={anyBusy || !started || suspended}
                          sx={{ color: "#f59e0b", "&:hover": { bgcolor: "#fffbeb" }, "&.Mui-disabled": { opacity: .3 } }}>
                          <PauseIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                      <Tooltip title={canTelnet ? t("open_telnet", { defaultValue: "Console Telnet" }) : t("telnet_disabled_node", { defaultValue: "Noeud non demarre" })}><span>
                        <IconButton size="small" onClick={() => openTelnet(n)} disabled={!canTelnet}
                          sx={{ color: "#6366f1", "&:hover": { bgcolor: "#eef2ff" }, "&.Mui-disabled": { opacity: .3 } }}>
                          <TerminalIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                      <Tooltip title={t("web_console", { defaultValue: "Console web" })}><span>
                        <IconButton size="small" onClick={() => openWeb(n)} disabled={!canWeb}
                          sx={{ "&:hover": { bgcolor: "#f0f9ff" }, "&.Mui-disabled": { opacity: .3 } }}>
                          <LanguageIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                      <Tooltip title={canVnc ? t("vnc_label", { defaultValue: "VNC" }) : t("vnc_disabled_node", { defaultValue: "VNC non disponible" })}><span>
                        <IconButton size="small" onClick={() => openVnc(n)} disabled={!canVnc}
                          sx={{ "&:hover": { bgcolor: "#f5f3ff" }, "&.Mui-disabled": { opacity: .3 } }}>
                          <DesktopWindowsIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Stack>

        {/* Topologie */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TopologyViewer
            nodes={topoNodes} links={topoLinks}
            loading={topoLoading} error={topoError}
            onNodeClick={handleTopologyNodeClick}
            onRefresh={loadTopology}
          />
        </Box>
      </Stack>

      {/* Consoles */}
      <ConsoleManager
        reservationId={rid} host={host} nodes={nodes}
        onApiReady={api => { consoleApiRef.current = api; }}
      />

      {/* NetBot flottant */}
      <NetBotWidget defaultMode="lab_debugger" context={netbotContext} />

      {/* Dialog prolonger */}
      <ExtendDialog open={extendOpen} onClose={() => setExtendOpen(false)} onConfirm={handleExtend} t={t} />

      {/* Snackbar alertes */}
      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack("")}
        message={snack} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />

      <ContextualOnboarding
        pageKey="lab_workspace"
        steps={[
          {
            title: t("onboarding.lab_workspace.step1_title", { ns: "common" }),
            text: t("onboarding.lab_workspace.step1_text", { ns: "common" }),
            targetRef: nodesSectionRef,
            placement: "bottom",
          },
          {
            title: t("onboarding.lab_workspace.step2_title", { ns: "common" }),
            text: t("onboarding.lab_workspace.step2_text", { ns: "common" }),
            targetRef: extendButtonRef,
            placement: "bottom",
          },
        ]}
      />

    </Box>
  );
}
